import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

interface LeadClassifiedPackage {
  _id: string;
  name: string;
  price: string;
  duration: string;
  users: string;
  leadAccess: string;
  dailyUnlock: string;
  isActive: boolean;
}

export default function AdminLeadClassifiedPackages() {
  const { token } = useAuth();
  const [packages, setPackages] = useState<LeadClassifiedPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Custom delete confirmation states
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('');
  const [users, setUsers] = useState('');
  const [leadAccess, setLeadAccess] = useState('');
  const [dailyUnlock, setDailyUnlock] = useState('');

  const fetchPackages = async () => {
    try {
      if (!token) return;
      setLoading(true);
      const data = await api.adminGetLeadClassifiedPackages(token);
      setPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to fetch lead classified packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [token]);

  const openAddModal = () => {
    setEditId(null);
    setName('');
    setPrice('');
    setDuration('');
    setUsers('');
    setLeadAccess('');
    setDailyUnlock('');
    setShowModal(true);
  };

  const openEditModal = (pkg: LeadClassifiedPackage) => {
    setEditId(pkg._id);
    setName(pkg.name);
    setPrice(pkg.price);
    setDuration(pkg.duration);
    setUsers(pkg.users);
    setLeadAccess(pkg.leadAccess);
    setDailyUnlock(pkg.dailyUnlock);
    setShowModal(true);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!token || !deleteId) return;
    try {
      await api.adminDeleteLeadClassifiedPackage(token, deleteId);
      toast.success('Package deleted successfully');
      setDeleteId(null);
      fetchPackages();
    } catch (err) {
      toast.error('Failed to delete package');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const payload = {
      name,
      price,
      duration,
      users,
      leadAccess,
      dailyUnlock
    };

    try {
      if (editId) {
        await api.adminUpdateLeadClassifiedPackage(token, editId, payload);
        toast.success('Package updated successfully');
      } else {
        await api.adminCreateLeadClassifiedPackage(token, payload);
        toast.success('Package created successfully');
      }
      setShowModal(false);
      fetchPackages();
    } catch (err) {
      toast.error('Failed to save package');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 bg-slate-50 min-h-screen" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-[13px] font-extrabold tracking-wider text-blue-600 uppercase mb-1">Classified Hub</p>
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#05144b] leading-tight">Lead Classified Packages</h1>
              <p className="text-sm text-gray-500 font-medium mt-1">Manage dynamic packages easily anytime.</p>
            </div>

            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#05144b] text-white font-bold text-[14px] hover:bg-blue-900 transition-all active:scale-95 shadow-lg shadow-blue-900/20 w-fit self-end md:self-auto"
            >
              <i className="fa-solid fa-plus text-sm"></i>
              Add Package
            </button>
          </div>

          {/* Packages List */}
          {loading ? (
            <div className="p-16 text-center font-bold text-[#05144b] animate-pulse text-sm">
              Fetching lead classified packages...
            </div>
          ) : packages.length === 0 ? (
            <div className="p-16 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <i className="fa-solid fa-box-open text-4xl text-gray-300 mb-4 block animate-bounce"></i>
              <p className="font-bold text-[#05144b] text-base mb-1">No Lead Classified Packages found</p>
              <p className="text-gray-400 mt-1 text-sm font-medium">Click "Add Package" to create your first dynamic plan.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg._id}
                  className="p-6 rounded-2xl border border-gray-200/80 bg-white hover:border-blue-400/60 hover:shadow-xl hover:shadow-blue-50/40 transition-all duration-300 group flex flex-col justify-between"
                >
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 text-xl font-bold shadow-sm group-hover:scale-110 transition-all duration-300">
                        <i className="fa-solid fa-crown"></i>
                      </div>
                      <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full font-bold text-[11px] tracking-wide">Dynamic Plan</span>
                    </div>

                    <h3 className="text-xl font-extrabold text-[#05144b] mb-1 group-hover:text-blue-700 transition-colors duration-300">{pkg.name}</h3>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-2xl font-black text-blue-600">{pkg.price}</span>
                      <span className="text-[13px] font-bold text-gray-400">/ {pkg.duration}</span>
                    </div>

                    <div className="border-t border-gray-100 my-4"></div>

                    {/* Features list */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <i className="fa-solid fa-user-group text-blue-500 w-5"></i>
                        <span className="text-[13px] font-semibold text-gray-700">{pkg.users}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="fa-solid fa-unlock-keyhole text-blue-500 w-5"></i>
                        <span className="text-[13px] font-semibold text-gray-700">Lead Access: {pkg.leadAccess}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="fa-solid fa-bolt text-blue-500 w-5"></i>
                        <span className="text-[13px] font-semibold text-gray-700">Daily Unlock: {pkg.dailyUnlock}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 border-t border-gray-100 pt-4 mt-auto">
                    <button
                      onClick={() => openEditModal(pkg)}
                      className="flex-1 py-3 rounded-xl bg-white border-2 border-blue-100 hover:border-blue-600 hover:bg-blue-50/40 text-[#05144b] hover:text-blue-700 text-[13px] font-bold transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                      <i className="fa-solid fa-pen-to-square"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(pkg._id)}
                      className="py-3 px-4 rounded-xl bg-white border-2 border-rose-50 hover:border-rose-600 hover:bg-rose-50/50 text-gray-400 hover:text-rose-600 text-[13px] font-bold transition-all active:scale-95"
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate__animated animate__fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 md:p-8 shadow-2xl relative border border-gray-100 animate__animated animate__zoomIn">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-[#05144b]">{editId ? 'Edit Package' : 'Add New Package'}</h2>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">Customize your package values dynamically.</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 rounded-xl bg-slate-50 text-gray-500 hover:bg-rose-50 hover:text-rose-600 transition-all font-bold flex items-center justify-center text-lg shadow-sm"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Plan Name</label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="🔥 Quarterly"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Price</label>
                    <input
                      type="text"
                      required
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="₹1,499"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Duration</label>
                    <input
                      type="text"
                      required
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder="3 Months"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Users</label>
                    <input
                      type="text"
                      required
                      value={users}
                      onChange={(e) => setUsers(e.target.value)}
                      placeholder="1 User"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Lead Access</label>
                    <input
                      type="text"
                      required
                      value={leadAccess}
                      onChange={(e) => setLeadAccess(e.target.value)}
                      placeholder="Unlimited"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">Daily Unlock</label>
                    <input
                      type="text"
                      required
                      value={dailyUnlock}
                      onChange={(e) => setDailyUnlock(e.target.value)}
                      placeholder="Unlimited"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm transition-all duration-300 text-slate-800 font-semibold"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-3.5 rounded-xl border-2 border-gray-100 hover:bg-slate-50 text-gray-500 font-bold text-sm transition-all active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3.5 rounded-xl bg-[#05144b] text-white font-bold text-sm hover:bg-blue-900 transition-all active:scale-95 shadow-lg shadow-blue-900/20"
                  >
                    {editId ? 'Save Changes' : 'Create Package'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Custom Delete Confirmation Modal */}
        {deleteId && (
          <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate__animated animate__fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 md:p-8 shadow-2xl relative border border-gray-100 animate__animated animate__zoomIn text-center">
              <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-600 text-2xl mx-auto mb-4 animate-pulse">
                <i className="fa-solid fa-circle-exclamation"></i>
              </div>
              <h2 className="text-xl font-black text-[#05144b] mb-2">Are you sure?</h2>
              <p className="text-sm text-gray-500 font-medium mb-6">Do you really want to delete this package? This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-100 hover:bg-slate-50 text-gray-500 font-bold text-sm transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm transition-all active:scale-95 shadow-lg shadow-rose-600/20"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
